// frontend/src/lib/setup/grades.ts
/** Setup ③ grade helpers. Bands use PaperDetail's ScoreBadge thresholds. */

export type Band = "strong" | "maybe" | "off";
export type Mark = "right" | "wrong";
export type Verdict = "too_high" | "too_low" | "right";

export function band(score: number): Band {
  return score >= 80 ? "strong" : score >= 50 ? "maybe" : "off";
}

export const BAND_BADGE: Record<Band, string> = {
  strong: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  maybe: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  off: "bg-muted text-muted-foreground",
};

export const BAND_DOT: Record<Band, string> = {
  strong: "bg-green-400",
  maybe: "bg-yellow-400",
  off: "bg-muted-foreground",
};

export function bandCounts(scores: number[]): Record<Band, number> {
  const c: Record<Band, number> = { strong: 0, maybe: 0, off: 0 };
  for (const s of scores) c[band(s)]++;
  return c;
}

export function bandSummary(c: Record<Band, number>): string {
  return [
    c.strong && `${c.strong} strong`,
    c.maybe && `${c.maybe} maybe`,
    c.off && `${c.off} off-topic`,
  ].filter(Boolean).join(" · ");
}

/** ✕ on a paper scored 50+ means it scored too high; below 50, too low. */
export function verdictFor(mark: Mark, score: number): Verdict {
  if (mark === "right") return "right";
  return score >= 50 ? "too_high" : "too_low";
}

const WORDS = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];
export function countWord(n: number): string {
  return WORDS[n] ?? String(n);
}

export function shortTitle(title: string, max = 60): string {
  return title.length > max ? `${title.slice(0, max - 1)}…` : title;
}

export function markedLine(wrong: { title: string; score: number }[]): { lead: string; bullets: string[] } {
  const item = (w: { title: string; score: number }) => `“${shortTitle(w.title)}” scored ${w.score}`;
  if (wrong.length === 1) return { lead: `You marked 1 as wrong: ${item(wrong[0])}.`, bullets: [] };
  return { lead: `You marked ${wrong.length} as wrong.`, bullets: wrong.slice(0, 3).map(item) };
}
